import { Card } from "@rothoblaas/design-system";
import { memo, useCallback, useId, useMemo, useState, type FC } from "react";
import { useFormContext } from "react-hook-form";
import useI18n from "../../../hooks/useTranslations";
import { SECONDARY_ITEM_CODE_PROFID, SECONDARY_ITEM_CODE_SNAP } from "../../../http/deckDesigner/types/secondaryItems";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import type { Glyph } from "../../../types";
import KkScrewGraphic from "./connectorFamilyGraphics/KkScrew";
import SecondaryProfidGraphic from "./connectorFamilyGraphics/SecondaryProfid";
import SecondarySnapGraphic from "./connectorFamilyGraphics/SecondarySnap";
import styles from "./FinishedDeckSectionCard.module.css";
import FinishedDeckSectionConnectors from "./FinishedDeckSectionConnectors";
import FinishedDeckSectionDefs, { getFinishedDeckSectionPaintIds } from "./FinishedDeckSectionDefs";
import { SUBSTRUCTURE_BORDER_WIDTH } from "./finishedDeckSectionGeometry";
import { useFinishedDeckSectionGeometry } from "./useFinishedDeckSectionGeometry";
import ZoomableSectionPreview, { getPreviewSize, type SectionPreviewSize } from "./ZoomableSectionPreview";

type FinishedDeckSectionCardProps = {
  className?: string;
  embedded?: boolean;
};

const SUBSTRUCTURE_BORDER_COLOR = "#626b72";
const BOARD_CUTOUT_BLEED_PX = 1;

const secondaryItemGraphicByCode: Record<string, Glyph> = {
  [SECONDARY_ITEM_CODE_PROFID]: SecondaryProfidGraphic,
  [SECONDARY_ITEM_CODE_SNAP]: SecondarySnapGraphic,
};

// The finished deck's side section, drawn to scale from the form (see finishedDeckSectionGeometry).
const FinishedDeckSectionCard: FC<FinishedDeckSectionCardProps> = ({ className, embedded = false }) => {
  const i18n = useI18n();
  // Per instance: an off-screen copy is mounted for the PDF export while the side view is open, and
  // shared ids would make its url(#...) references resolve to the visible instance's defs.
  const sectionId = useId().replaceAll(":", "");
  const paintIds = useMemo(() => getFinishedDeckSectionPaintIds(sectionId), [sectionId]);
  const boardCutoutMaskId = `${sectionId}-finished-section-board-cutouts`;
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();
  const [previewSize, setPreviewSize] = useState(() => getPreviewSize());
  const handlePreviewSizeChange = useCallback((nextSize: SectionPreviewSize) => {
    setPreviewSize((currentSize) =>
      Math.abs(currentSize.width - nextSize.width) < 0.5 && Math.abs(currentSize.height - nextSize.height) < 0.5
        ? currentSize
        : nextSize,
    );
  }, []);
  const { layout, resetKey } = useFinishedDeckSectionGeometry(control, previewSize);
  const { boards, connectors, gapSegments, groove, screws, secondary, substructure, support, toPixels, tvmLowerGap } =
    layout;
  const SecondaryItemGraphic = secondary.item ? secondaryItemGraphicByCode[secondary.item.item.code] : null;
  const title = i18n.translation.deckDesignerModule.configurations.calculation.sideView;
  const content = (
    <ZoomableSectionPreview
      ariaLabel={title}
      onPreviewSizeChange={handlePreviewSizeChange}
      previewSize={previewSize}
      resetKey={resetKey}
    >
      <defs>
        <FinishedDeckSectionDefs ids={paintIds} />
        <mask id={boardCutoutMaskId} maskUnits="userSpaceOnUse">
          <rect x="0" y="0" width={previewSize.width} height={previewSize.height} fill="white" />
          {tvmLowerGap.isVisible &&
            gapSegments.map((gapSegment) => (
              <rect
                key={`${gapSegment.key}-tvm-lower-gap`}
                x={gapSegment.centerX - tvmLowerGap.width / 2 - BOARD_CUTOUT_BLEED_PX}
                y={tvmLowerGap.y - BOARD_CUTOUT_BLEED_PX}
                width={tvmLowerGap.width + BOARD_CUTOUT_BLEED_PX * 2}
                height={boards.bottom - tvmLowerGap.y + BOARD_CUTOUT_BLEED_PX * 2}
                fill="black"
                shapeRendering="crispEdges"
              />
            ))}
          {groove.isVisible &&
            gapSegments.map((gapSegment) => (
              <g key={`${gapSegment.key}-grooves`}>
                <rect
                  x={gapSegment.leftX - groove.depth - BOARD_CUTOUT_BLEED_PX}
                  y={groove.y - BOARD_CUTOUT_BLEED_PX}
                  width={groove.depth + BOARD_CUTOUT_BLEED_PX * 2}
                  height={groove.height + BOARD_CUTOUT_BLEED_PX * 2}
                  fill="black"
                  shapeRendering="crispEdges"
                />
                <rect
                  x={gapSegment.rightX - BOARD_CUTOUT_BLEED_PX}
                  y={groove.y - BOARD_CUTOUT_BLEED_PX}
                  width={groove.depth + BOARD_CUTOUT_BLEED_PX * 2}
                  height={groove.height + BOARD_CUTOUT_BLEED_PX * 2}
                  fill="black"
                  shapeRendering="crispEdges"
                />
              </g>
            ))}
        </mask>
      </defs>

      {support && (
        <support.Graphic
          className={support.isFullWidth ? undefined : styles.supportGraphic}
          x={support.x}
          y={support.y}
          width={support.width}
          height={support.height}
          heightMm={support.heightMm}
          widthMm={support.widthMm}
          preserveAspectRatio="none"
          {...support.familyProps}
        />
      )}

      <g className={styles.substructure}>
        <rect
          x={substructure.x}
          y={substructure.y}
          width={substructure.width}
          height={substructure.height}
          fill={`url(#${substructure.isAluminium ? paintIds.aluminiumBaseGradient : paintIds.woodBaseGradient})`}
        />
        <rect
          x={substructure.x}
          y={substructure.y}
          width={substructure.width}
          height={substructure.height}
          fill={`url(#${
            substructure.isAluminium ? paintIds.aluminiumBrushPattern : paintIds.woodSubstructureGrainPattern
          })`}
        />
        <rect
          x={substructure.x + substructure.borderInset}
          y={substructure.y + substructure.borderInset}
          width={substructure.borderWidth}
          height={substructure.borderHeight}
          fill="none"
          stroke={SUBSTRUCTURE_BORDER_COLOR}
          strokeWidth={SUBSTRUCTURE_BORDER_WIDTH}
        />
      </g>

      <g className={styles.boards} mask={`url(#${boardCutoutMaskId})`}>
        {boards.segments.map((boardSegment) => (
          <g key={boardSegment.key}>
            <rect
              x={boardSegment.x}
              y={boards.top}
              width={boardSegment.width}
              height={boards.height}
              fill={`url(#${paintIds.woodBaseGradient})`}
            />
            <rect
              x={boardSegment.x}
              y={boards.top}
              width={boardSegment.width}
              height={boards.height}
              fill={`url(#${paintIds.woodGrainPattern})`}
            />
          </g>
        ))}
      </g>

      {secondary.item && !secondary.isSnap && (
        <g className={styles.secondaryStrip}>
          {SecondaryItemGraphic ? (
            <SecondaryItemGraphic
              className={styles.secondaryGraphic}
              x={substructure.x}
              y={secondary.y}
              width={substructure.width}
              height={secondary.height}
              preserveAspectRatio="none"
            />
          ) : (
            <rect x={substructure.x} y={secondary.y} width={substructure.width} height={secondary.height} />
          )}
        </g>
      )}

      {connectors && (
        <FinishedDeckSectionConnectors connectors={connectors} gapSegments={gapSegments} toPixels={toPixels} />
      )}

      {screws.connectorScrewPoints?.map((screwPoint) => (
        <KkScrewGraphic
          key={screwPoint.key}
          className={styles.screwConnector}
          screw={screws.connectorScrew}
          toPixels={toPixels}
          centerX={screwPoint.centerX}
          substructureMaterialCode={screws.substructureMaterialCode}
          topY={boards.top}
        />
      ))}

      {screws.visibleFasteningScrewPoints?.map((screwPoint) => (
        <KkScrewGraphic
          key={screwPoint.key}
          className={styles.screwConnector}
          screw={screws.visibleFasteningScrew}
          toPixels={toPixels}
          centerX={screwPoint.centerX}
          substructureMaterialCode={screws.substructureMaterialCode}
          headBottomY={boards.top}
        />
      ))}

      {secondary.item && secondary.isSnap && (
        <g className={styles.secondaryStrip}>
          {secondary.snapSegments.map((snapSegment) => (
            <SecondarySnapGraphic
              key={snapSegment.key}
              className={styles.secondaryGraphic}
              x={snapSegment.leftX}
              y={secondary.y - secondary.snapHeadHeight}
              width={secondary.snapWidth}
              height={secondary.snapHeight}
              preserveAspectRatio="xMidYMid meet"
            />
          ))}
        </g>
      )}
    </ZoomableSectionPreview>
  );

  return embedded ? (
    content
  ) : (
    <Card
      style={{ minHeight: 400 }}
      className={className}
      accentColor="var(--accent-base, var(--red))"
      shadow
      title={title}
    >
      {content}
    </Card>
  );
};

// Memoized: its props are stable, so it re-renders through its own watched fields and props only, not
// whenever its parent does.
export default memo(FinishedDeckSectionCard);
